// NPM imports
import React, { Component } from "react";
import { connect } from "react-redux";
import { debounce } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  NumberInput,
  Tooltip,
  Button,
  Label,
  Input,
} from "../../../revvComponents";

// Components Imports

// Actions Imports
import PricebooksActions from "../../../actions/PricebooksActions";

// Service Imports
import { searchProductForPOT } from "../../../actions/PricebooksActions/pricebooksService";

// Destructuring
const { updatePricingMethodData } = PricebooksActions;

// Component

class PercentageOfTotal extends Component {
  searchDropDownRef = React.createRef();

  //* Make the Search API on 1 sec timeInterval using lodash debunce
  delaySearch = debounce(searchTxt => {
    this._onSearch(searchTxt);
  }, 1000);

  //! LIFECYCLE FUNCTIONS

  componentDidMount() {
    if (this.props.isEdit) {
      this._updateState({
        showSearch: false,
      });
    }
  }

  // ==========================================================================
  //! SEARCH FUNCTIONS

  _onSearch = searchTxt => {
    searchProductForPOT(this.props.metaData.pricelistId, searchTxt)
      .then(res => {
        /**
         * Same Product Can't be added to POT product items, meaning for SKU CAT9300 POT is selected
         * as pricing type, now this same SKU can't be added to its product calculation list
         */
        const payload = res.filter(
          product => product.product_id !== this.props.product.id,
        );
        this._updateState({ products: payload });
      })
      .catch(error => console.error(error));
  };

  _onSearchTxtChange = event => {
    event.persist();
    this._updateState({ searchTxt: event.target.value });
    this.delaySearch(event.target.value);
  };

  _toggleSearch = () => {
    this._updateState({
      showSearch: !this.props.data.showSearch,
      products: [],
    });
  };

  _handleSearchInputClick = () => {
    // attach/remove event handler
    if (!this.props.data.showSuggestion) {
      document.addEventListener("click", this._handleOutsideClick, false);
    } else {
      document.removeEventListener("click", this._handleOutsideClick, false);
    }

    this._updateState({
      showSuggestion: !this.props.data.showSuggestion,
    });
  };

  _handleOutsideClick = e => {
    const node = this.searchDropDownRef.current;
    // ignore clicks on the component itself
    if (node && node.contains(e.target)) {
      return;
    }
    this._handleSearchInputClick();
  };

  // ==========================================================================
  //! ACTION FUNCTIONS

  _updateState = newState => {
    this.props.updatePricingMethodData("percentageOfTotal", newState);
  };

  _deleteEntry = index => {
    const clonePriceListItems = [...this.props.data.priceListItems];
    clonePriceListItems.splice(index, 1);
    this._updateState({
      priceListItems: clonePriceListItems,
      products: [],
      searchTxt: "",
    });
  };

  _onProductSelect = data => {
    const cloneState = { ...this.props.data };
    //* To Handle POT Backward Compatible
    if (cloneState.priceListItems) {
      cloneState.priceListItems.push(data);
    } else {
      cloneState.priceListItems = [data];
    }
    this._updateState({
      priceListItems: cloneState.priceListItems,
      searchTxt: "",
      showSearch: false,
    });
  };

  // ==========================================================================
  //! UI RELATED FUNCTIONS

  _decideView = () => {
    if (this.props.data.products && this.props.data.products.length > 0) {
      return this.props.data.products.map(product => (
        <div
          className="percentageoftotal-suggestion-item"
          key={product.pricelist_item_id}
          onClick={() => this._onProductSelect(product)}
          onKeyPress={() => this._onProductSelect(product)}
          role="button"
          tabIndex="0">
          {product.product_name}
        </div>
      ));
    }
    return (
      <div className="percentageoftotal-search-empty">
        Start typing to see suggestions
      </div>
    );
  };

  _isBtnDisabled = () => {
    const { priceListItems = [], showSearch } = this.props.data;
    let isDisabled = true;
    if (priceListItems.length !== 0 && !showSearch) {
      isDisabled = false;
    }
    return isDisabled;
  };
  // ==========================================================================

  render() {
    const {
      priceListItems = [],
      searchTxt = "",
      showSearch,
      showSuggestion,
      sourceColumn,
      value,
    } = this.props.data;
    return (
      <div className="percentageoftotal-wrapper">
        {/** SECTION TO INPUT PERCENTAGE VALUE */}
        <section className="percentageoftotal-percent-input">
          <span className="revvform-label">Enter percentage value</span>
          <NumberInput
            className="percentageoftotal-input"
            isFloat
            limitTo={100}
            placeholder="Enter Percentage"
            value={value}
            onChange={percentageValue =>
              this.props.updatePricingMethodData("percentageOfTotal", {
                value: percentageValue,
              })
            }
            data-test="percentageoftotal-number-input"
          />
        </section>

        {/** SECTION TO SELECT SOURCE COLUMN */}
        <section className="percentageoftotal-products-wrapper">
          <span className="percentageoftotal-products-title">
            Choose base for PoT calculation
          </span>
          <div>
            <span className="percentageoftotal-base-options">
              <Label htmlFor="">
                <Input
                  type="radio"
                  name="choose_source"
                  value=""
                  checked={sourceColumn === "ext_base_price"}
                  onChange={() =>
                    this.props.updatePricingMethodData("percentageOfTotal", {
                      sourceColumn: "ext_base_price",
                    })
                  }
                  data-test="percentageoftotal-base-price-radio-input"
                />
                Extended Base price Total
              </Label>
            </span>
            <span className="percentageoftotal-base-options">
              <Label htmlFor="">
                <Input
                  type="radio"
                  name="choose_source"
                  value=""
                  checked={sourceColumn === "net_price"}
                  onChange={() =>
                    this.props.updatePricingMethodData("percentageOfTotal", {
                      sourceColumn: "net_price",
                    })
                  }
                  data-test="percentageoftotal-net-price-radio-input"
                />
                Net price Total
              </Label>
            </span>
          </div>
        </section>

        {/** SECTION TO ATTACH PRODUCTS */}
        <section>
          <div className="percentageoftotal-productsearch-wrapper">
            <span className="percentageoftotal-left-section">Product Name</span>
            <span className="percentageoftotal-right-section">SKU Code</span>
          </div>

          {/** SECTION TO DISPLAY THE SELECTED PRODUCTS */}
          {priceListItems.length > 0 &&
            priceListItems.map((product, index) => (
              <div
                className="percentageoftotal-product-item"
                key={product.pricelist_item_id}>
                <div className="percentageoftotal-left-section percentageoftotal-input is-disabled">
                  {product.product_name}
                </div>
                <div className="percentageoftotal-right-section percentageoftotal-input is-disabled">
                  {product.sku}
                </div>
                <div
                  className="percentageoftotal-remove-product"
                  onKeyPress={() => null}
                  role="button"
                  tabIndex="0"
                  onClick={() => this._deleteEntry(index)}>
                  <Tooltip placement="top" overlay="Remove Product">
                    <FontAwesomeIcon icon={["far", "times-circle"]} />
                  </Tooltip>
                </div>
              </div>
            ))}

          {/** SECTION TO DISPLAY PRODUCT INPUT SEARCH */}
          {(priceListItems.length === 0 || showSearch) && (
            <div className="percentageoftotal-search-wrapper">
              <Input
                className="percentageoftotal-search-left"
                type="text"
                value={searchTxt}
                placeholder="Search"
                onChange={this._onSearchTxtChange}
                onClick={this._handleSearchInputClick}
                data-test="percentageoftotal-skucode-input"
              />
              <div className="percentageoftotal-search-right percentageoftotal-sku-input">
                SKU Code
              </div>
              {showSuggestion && (
                <div
                  className="percentageoftotal-search-suggestion"
                  ref={this.searchDropDownRef}>
                  {this._decideView()}
                </div>
              )}
            </div>
          )}

          {/** SECTION TO DISPLAY ADD SKU BUTTON */}
          <div className="percentageoftotal-add-product">
            <Button
              icon={["far", "plus-circle"]}
              disabled={this._isBtnDisabled()}
              onClick={this._toggleSearch}
              data-test="percentageoftotal-add-skucode-btn">
              ADD SKU
            </Button>
          </div>
        </section>
      </div>
    );
  }
}

const mapStateToProps = store => {
  const {
    pricingMethodData: { percentageOfTotal: data, product } = {}, // Targed nested keys and set use a specific name for it => priceingMethodData.percentageOfTotal = data
    metaData,
  } = store.PricebookStore;
  return {
    data,
    metaData,
    product,
  };
};

const mapDispatchToProps = {
  updatePricingMethodData,
};

export default connect(mapStateToProps, mapDispatchToProps)(PercentageOfTotal);
